import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Activity, TrendingUp, AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import { getAdminUsuariosStats } from "../../api";
import SideBarNavUsuarios from "../../componets/SideBarNavUsuarios";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function BarMini({ value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div
          className="h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-semibold text-gray-700 w-5 text-right">{value}</span>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color}`}>{icon}</div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtro, setFiltro] = useState("");

  async function carregar() {
    setLoading(true);
    setErro(null);
    try {
      const res = await getAdminUsuariosStats();
      setDados(res);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { carregar(); }, []);

  const usuarios = dados?.usuarios ?? [];
  const resumo = dados?.resumo ?? {};
  const max7d = Math.max(...usuarios.map((u) => u.logins_7_dias), 1);

  const usuariosFiltrados = usuarios.filter((u) => {
    const q = filtro.toLowerCase();
    return u.nome.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q);
  });

  const usuariosOrdenados = [...usuariosFiltrados].sort((a, b) => b.logins_7_dias - a.logins_7_dias);

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <SideBarNavUsuarios isLight />

      <div className="flex flex-col flex-1 bg-gray-50" style={{ overflow: "auto" }}>
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="p-1.5 rounded-lg hover:bg-white/10 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-lg font-bold tracking-tight">Painel Admin · Usuários</span>
        </div>
          <button onClick={() => navigate("/inicio")} style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
          }}>
            Menu Principal
          </button>
      </header>

      <main className="flex-1 px-6 py-8 max-w-7xl mx-auto w-full">
        {/* Cards de resumo */}
        {!loading && !erro && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={<Users className="w-6 h-6 text-white" />}
              label="Total de Usuários"
              value={resumo.total_usuarios ?? 0}
              color="bg-purple-600"
            />
            <StatCard
              icon={<Activity className="w-6 h-6 text-white" />}
              label="Usuários Ativos"
              value={resumo.usuarios_ativos ?? 0}
              color="bg-emerald-500"
            />
            <StatCard
              icon={<TrendingUp className="w-6 h-6 text-white" />}
              label="Logins Hoje"
              value={resumo.logins_hoje ?? 0}
              color="bg-pink-500"
            />
            <StatCard
              icon={<TrendingUp className="w-6 h-6 text-white" />}
              label="Logins (7 dias)"
              value={resumo.logins_7_dias ?? 0}
              color="bg-indigo-500"
            />
          </div>
        )}

        {/* Tabela */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-base font-semibold text-gray-800">Atividade por Usuário</h2>
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Buscar nome, e-mail ou role…"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-pink-400 w-64"
              />
              <button
                onClick={carregar}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-pink-600 transition"
              >
                <RefreshCw className="w-4 h-4" />
                Atualizar
              </button>
            </div>
          </div>

          {loading && (
            <div className="py-16 text-center text-gray-400 text-sm">Carregando dados…</div>
          )}

          {erro && (
            <div className="py-12 text-center">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-500">{erro}</p>
            </div>
          )}

          {!loading && !erro && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuário</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Último Login</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-48">Logins 7 dias</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Hoje</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {usuariosOrdenados.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3">
                        <p className="font-medium text-gray-800">{u.nome}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-purple-50 text-purple-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {u.bloqueado ? (
                          <span className="bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full">Bloqueado</span>
                        ) : u.ativo ? (
                          <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">Ativo</span>
                        ) : (
                          <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full">Inativo</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(u.ultimo_login)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-700">{u.total_logins}</td>
                      <td className="px-6 py-3">
                        <BarMini value={u.logins_7_dias} max={max7d} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {u.logins_hoje > 0 ? (
                          <span className="bg-pink-100 text-pink-700 text-xs font-bold px-2 py-0.5 rounded-full">{u.logins_hoje}</span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {usuariosOrdenados.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-10 text-center text-gray-400 text-sm">
                        Nenhum usuário encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <footer className="text-center text-xs text-gray-400 py-6">
        © {new Date().getFullYear()} Youx Group · Painel Administrativo
      </footer>
      </div>
    </div>
  );
}
